import type { TenantOverviewRow } from "@repo/shared/types";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "@/components/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTableSort } from "@/hooks/use-table-sort";
import { useAdminAccess, useTenantsOverview } from "@/lib/hooks/use-admin";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./page.i18n";

type SortKey = keyof Pick<
  TenantOverviewRow,
  "name" | "slug" | "users" | "yau" | "mau" | "dau" | "flows" | "clients"
>;

export default function TenantsOverviewPage() {
  const navigate = useNavigate();
  const { isLoading: accessLoading, canViewAdmin: canView } = useAdminAccess();
  const { tenants, isLoading: dataLoading } = useTenantsOverview();
  const t = useT(T);

  const isLoading = accessLoading || dataLoading;

  useEffect(() => {
    if (isLoading) return;
    if (!canView) {
      navigate("/", { replace: true });
    }
  }, [isLoading, canView, navigate]);

  const { sorted, toggleSort, directionFor } = useTableSort(tenants, "name");

  if (isLoading || !canView) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const columns: { key: SortKey; label: string; numeric?: boolean }[] = [
    { key: "name", label: t.name },
    { key: "slug", label: t.slug },
    { key: "users", label: t.users, numeric: true },
    { key: "yau", label: t.yau, numeric: true },
    { key: "mau", label: t.mau, numeric: true },
    { key: "dau", label: t.dau, numeric: true },
    { key: "flows", label: t.flows, numeric: true },
    { key: "clients", label: t.clients, numeric: true },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.admin, href: "/admin" }, { label: t.title }]}
        title={t.title}
        description={t.description}
      />

      {sorted.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">{t.noTenants}</p>
      ) : (
        <Table variant="data">
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <SortableTableHead
                  key={col.key}
                  className={col.numeric ? "text-right" : undefined}
                  direction={directionFor(col.key)}
                  onSort={() => toggleSort(col.key)}
                >
                  {col.label}
                </SortableTableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell className="text-muted-foreground">{row.slug}</TableCell>
                <TableCell className="text-right font-mono">{row.users}</TableCell>
                <TableCell className="text-right font-mono">{row.yau}</TableCell>
                <TableCell className="text-right font-mono">{row.mau}</TableCell>
                <TableCell className="text-right font-mono">{row.dau}</TableCell>
                <TableCell className="text-right font-mono">{row.flows}</TableCell>
                <TableCell className="text-right font-mono">{row.clients}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
